import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  api,
  clearToken,
  getToken,
  getUser,
  setToken,
  setUnauthorizedHandler,
  setUser as persistUser,
} from "./api";
import type { AuthUser } from "./api";
import { registerPush, unregisterPush } from "./native/push";

interface AuthContextValue {
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  /** Superadmin-only: re-scope the session to another tenant (full reload). */
  switchTenant: (tenantId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<AuthUser | null>(() => getUser());
  const navigate = useNavigate();

  useEffect(() => {
    // Wire api.ts's session-expiry callback to an in-SPA redirect instead of
    // its default hard window.location reload.
    setUnauthorizedHandler(() => {
      setUserState(null);
      navigate("/login", { replace: true });
    });
  }, [navigate]);

  const login = async (email: string, password: string) => {
    const result = await api.post<{ token: string; user: AuthUser }>("/api/auth/login", {
      email,
      password,
    });
    setToken(result.token);
    persistUser(result.user);
    setUserState(result.user);
    void registerPush(); // native only; no-op on web
  };

  const logout = () => {
    void unregisterPush(); // native only; no-op on web
    clearToken();
    setUserState(null);
    navigate("/login", { replace: true });
  };

  const switchTenant = async (tenantId: string) => {
    const result = await api.post<{ token: string; user: AuthUser }>(
      "/api/auth/switch-tenant",
      { tenantId }
    );
    setToken(result.token);
    persistUser(result.user);
    setUserState(result.user);
    // Hard reload so every screen refetches cleanly for the new tenant.
    window.location.assign("/");
  };

  const value = useMemo(() => ({ user, login, logout, switchTenant }), [user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function isLoggedIn(): boolean {
  return Boolean(getToken() && getUser());
}
